import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, HelpCircle, FolderOpen, LogOut, User, Printer, BadgeCheck, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const SECTOR_OPTIONS = [8, 16, 32];
const MENU_CLS = "bg-slate-900 border-slate-800 text-slate-200";
const ITEM_CLS = "cursor-pointer focus:bg-slate-800";
const LABEL_CLS = "text-slate-500 font-mono text-[10px] uppercase tracking-widest";

export default function AnalyzerHeader({ user, sectorCount, onSectorCount, onOpenPlans, onOpenBranding, onLogout, exports }) {
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <header className="flex items-center justify-between px-6 py-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur-md">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center gold-glow">
          <Compass className="h-5 w-5 text-slate-950" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display text-lg font-semibold tracking-tight leading-none">Vastu Mandala</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Floor Plan Sector Analyzer</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <SectorModeToggle value={sectorCount} onChange={onSectorCount} />
        <div className="h-6 w-px bg-slate-800 mx-1" />

        <Button variant="outline" size="sm" data-testid="my-plans-btn" onClick={onOpenPlans}
          className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-200 h-9">
          <FolderOpen className="h-4 w-4 mr-1.5" /> My Plans
        </Button>

        <ExportMenu {...exports} />
        <HelpDialog />

        <div className="h-6 w-px bg-slate-800 mx-1" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" data-testid="user-menu-btn" className="text-slate-300 hover:text-slate-100 hover:bg-slate-800 h-9">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400 mr-1.5">
                <User className="h-3.5 w-3.5" />
              </div>
              <span className="max-w-[120px] truncate text-sm">{user?.name || user?.email}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={`${MENU_CLS} min-w-[220px]`}>
            <DropdownMenuLabel className={LABEL_CLS}>Signed in as</DropdownMenuLabel>
            <div className="px-2 pb-2">
              <div className="font-display text-sm text-slate-100 truncate">{user?.name}</div>
              <div className="text-xs text-slate-400 truncate">{user?.email}</div>
            </div>
            <DropdownMenuSeparator className="bg-slate-800" />
            {user?.role === "admin" && (
              <DropdownMenuItem data-testid="admin-panel-menu-item" onClick={() => navigate("/admin")} className={ITEM_CLS}>
                <ShieldCheck className="h-4 w-4 mr-2" /> Admin panel
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid="branding-menu-item" onClick={onOpenBranding} className={ITEM_CLS}>
              <BadgeCheck className="h-4 w-4 mr-2" /> Report branding
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="change-password-menu-item" onClick={() => setPwOpen(true)} className={ITEM_CLS}>
              <KeyRound className="h-4 w-4 mr-2" /> Change password
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="logout-menu-item" onClick={onLogout} className={`${ITEM_CLS} text-red-300 focus:text-red-200`}>
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}

function SectorModeToggle({ value, onChange }) {
  return (
    <div className="inline-flex bg-slate-950 p-1 rounded-lg border border-slate-800">
      {SECTOR_OPTIONS.map((n) => (
        <button key={n} data-testid={`sector-mode-${n}-button`} onClick={() => onChange(n)}
          className={`px-3 h-7 rounded-md font-mono text-xs font-semibold transition-colors ${value === n ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-100"}`}>
          {n}
        </button>
      ))}
    </div>
  );
}

function ExportMenu({ onPDF }) {
  return (
    <Button
      variant="outline" size="sm" data-testid="export-menu-btn" onClick={onPDF}
      className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-200 h-9"
    >
      <Printer className="h-4 w-4 mr-1.5" /> Export PDF
    </Button>
  );
}

const STEPS = [
  ["Load a floor plan", "Upload JPG, PNG, SVG or PDF — or click the Load Sample button in the toolbar."],
  ["Set True North", "Drag the True North slider — the plan stays fixed while the compass grid and north arrow rotate to match the real north on your site."],
  ["Draw the house outline", "In Draw mode, click each outer corner of the house to define the polygon. With Snap (magnet) on, points lock onto detected wall corners — hold Alt to place freely."],
  ["Brahmasthan appears", "The pink glowing center is computed automatically as the polygon centroid."],
  ["Place room markers", "Pick a room type (Kitchen, Pooja…) and click the plan — markers turn red when a room sits in a conflicting Vastu zone."],
  ["Explore & export", "Toggle 8/16/32 divisions — 32 uses the standard entrance padas N1–N8, E1–E8, S1–S8, W1–W8 with labels outside the outline. Hover to inspect, then export a branded print-ready PDF with a cover sheet and all three maps."],
];

function HelpDialog() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="help-btn" className="text-slate-400 hover:text-slate-100 hover:bg-slate-800 h-9 w-9 p-0">
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">How to use</DialogTitle>
          <DialogDescription className="text-slate-400">A quick guide to computing your Brahmasthan and Vastu sectors.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-slate-300 mt-2">
          {STEPS.map(([title, body], i) => (
            <li key={title} className="flex gap-3">
              <div className="shrink-0 w-6 h-6 rounded-md bg-amber-500/20 text-amber-400 font-mono font-semibold text-xs flex items-center justify-center mt-0.5">{i + 1}</div>
              <div>
                <div className="font-display font-semibold text-slate-100">{title}</div>
                <div className="text-slate-400 text-sm mt-0.5">{body}</div>
              </div>
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
